import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ExternalLink, CheckCircle2, AlertTriangle, Zap } from "lucide-react";
import { api } from "@/lib/api";
import PacManBar from "@/pages/worth/PacManBar";

export default function WhatnotSettings({ trigger }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState("");
  const [env, setEnv] = useState("production");
  const [taxId, setTaxId] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatnot/status");
      setStatus(data);
      setEnv(data.env || "production");
      setTaxId(data.default_taxonomy_id || "");
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const save = async () => {
    if (!token.trim()) {
      toast.error("Paste your Whatnot access token first");
      return;
    }
    setSaving(true);
    try {
      await api.post("/whatnot/settings", {
        access_token: token.trim(),
        env,
        default_taxonomy_id: taxId.trim(),
      });
      toast.success("Whatnot connected");
      setToken("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save credentials");
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Whatnot? Your drafts will stop pushing until you reconnect.")) return;
    try {
      await api.post("/whatnot/disconnect");
      toast.success("Disconnected");
      refresh();
    } catch (e) {
      toast.error("Could not disconnect");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <SheetTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" /> Whatnot push
            </SheetTitle>
            <Badge variant="outline" className="font-mono text-[10px] uppercase">{env}</Badge>
          </div>
          <SheetDescription>
            Push the current draft to Whatnot as an unpublished listing. Needs a Whatnot Seller API access token.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <PacManBar className="mt-6" />
        )}

        {!loading && (
          <div className="mt-5 space-y-4">
            <section className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Access token</h3>
                {status?.configured ? (
                  <Badge className="bg-emerald-600 text-white hover:bg-emerald-700">
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Connected
                  </Badge>
                ) : (
                  <Badge variant="secondary">Not connected</Badge>
                )}
              </div>

              {status?.configured && (
                <p className="mb-2 font-mono text-[11px] text-slate-500">
                  Saved: {status.token_preview}
                </p>
              )}

              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
                Whatnot access token
              </Label>
              <Input
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="wn_access_tk_... (production) or wn_access_tk_test_... (staging)"
                className="mt-1 font-mono text-xs"
                data-testid="whatnot-token-input"
              />
              <p className="mt-1 text-[10px] text-slate-500">
                Get this from Whatnot Seller API dashboard. Encrypted in MongoDB — never shown back in full.
              </p>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Environment</Label>
                  <Select value={env} onValueChange={setEnv}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="whatnot-env-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staging">Staging</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Default taxonomy</Label>
                  <Input
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                    placeholder="base64 node id"
                    className="mt-1 h-8 font-mono text-xs"
                    data-testid="whatnot-taxonomy-input"
                  />
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <Button
                  onClick={save}
                  disabled={saving}
                  size="sm"
                  className="flex-1 rounded-full bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="whatnot-save-button"
                >
                  {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                  Save
                </Button>
                {status?.configured && (
                  <Button
                    onClick={disconnect}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-red-200 text-red-700 hover:bg-red-50"
                    data-testid="whatnot-disconnect-button"
                  >
                    Disconnect
                  </Button>
                )}
              </div>
            </section>

            <section className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="mb-1 flex items-center gap-1 font-semibold">
                <AlertTriangle className="h-3.5 w-3.5" /> How to get your token
              </div>
              <p className="mb-2">
                Whatnot's Seller API is in a <strong>closed Developer Preview</strong> — there's no self-serve
                signup. You have to email the Whatnot team and get added manually.
              </p>
              <ol className="ml-4 list-decimal space-y-1">
                <li>
                  Email{" "}
                  <a
                    href="mailto:sellerapi@whatnot.com?subject=Seller%20API%20access%20request&body=Hi%20Whatnot%20team%2C%0A%0AI%27d%20like%20to%20request%20Seller%20API%20access.%0A%0ASeller%20username%3A%20%0AStore%20URL%3A%20%0AUse%20case%3A%20pushing%20listings%20from%20a%20third-party%20drafting%20tool%20(SnapList)%20into%20my%20Whatnot%20seller%20hub.%0ARequested%20scopes%3A%20productCreate%2C%20read%20inventory.%0A%0AThanks%21"
                    className="underline hover:text-amber-700"
                  >
                    sellerapi@whatnot.com
                  </a>{" "}
                  with your seller username, store URL, and that you want to use SnapList to draft and push listings.
                </li>
                <li>Review the GraphQL schema while you wait at{" "}
                  <a
                    href="https://developers.whatnot.com/docs/getting-started/introduction"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-amber-700"
                  >
                    developers.whatnot.com <ExternalLink className="inline h-3 w-3" />
                  </a>
                </li>
                <li>Once approved, Whatnot sends you an <code>access_token</code> (and a client ID + secret if you asked for OAuth). Paste the access token above and tap Save.</li>
                <li>Grab a default taxonomy node ID from the{" "}
                  <a
                    href="https://api.whatnot.com/seller-api/rest/product-taxonomy/US.txt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-amber-700"
                  >
                    US taxonomy list <ExternalLink className="inline h-3 w-3" />
                  </a>{" "}
                  (base64 of <code>ProductTaxonomyNode:…</code>) so pushes auto-classify.</li>
              </ol>
              <p className="mt-2 text-[11px] text-amber-800/80">
                Until the token is saved, the "Push to Whatnot" button on each draft will fail gracefully with a
                <em> &nbsp;not connected&nbsp;</em> toast — nothing else breaks.
              </p>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
